import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight,
  Home as HomeIcon,
  MessageCircle,
  Users,
  HelpingHand,
  Stethoscope,
  GraduationCap,
  Calendar,
  LifeBuoy,
  Sparkles,
  Bell,
  Search,
  CheckCircle2,
  Send,
  MapPin,
  Info,
  UserPlus,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BtcLogo from "../components/BtcLogo";
import { PUBLIC_PORTAL_PREVIEW } from "../constants/testIds";

// PHASE 2 — REAL ANONYMOUS PORTAL PREVIEW
// -----------------------------------------------------------------------------
// This page is served publicly and MUST NOT call authenticated endpoints.
// It talks only to /api/preview/* which returns dedicated static sample data
// with no database access (see /app/backend/preview_routes.py).
//
// Data boundary lives server-side. The frontend does not need to interpret
// authentication or "hide" real member data.
// -----------------------------------------------------------------------------

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Anonymous axios — no cookies/JWT — so we can never accidentally send real
// member credentials to preview endpoints.
const previewApi = axios.create({
  baseURL: `${API_BASE}/api/preview`,
  withCredentials: false,
});

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: HomeIcon, testId: PUBLIC_PORTAL_PREVIEW.tabDashboard, panelId: PUBLIC_PORTAL_PREVIEW.panelDashboard },
  { id: "messaging", label: "Messaging", icon: MessageCircle, testId: PUBLIC_PORTAL_PREVIEW.tabMessaging, panelId: PUBLIC_PORTAL_PREVIEW.panelMessaging },
  { id: "community", label: "Community", icon: Users, testId: PUBLIC_PORTAL_PREVIEW.tabCommunity, panelId: PUBLIC_PORTAL_PREVIEW.panelCommunity },
  { id: "resources", label: "Resources", icon: GraduationCap, testId: PUBLIC_PORTAL_PREVIEW.tabResources, panelId: PUBLIC_PORTAL_PREVIEW.panelResources },
  { id: "events", label: "Events", icon: Calendar, testId: PUBLIC_PORTAL_PREVIEW.tabEvents, panelId: PUBLIC_PORTAL_PREVIEW.panelEvents },
  { id: "hospitals", label: "Hospitals", icon: Stethoscope, testId: PUBLIC_PORTAL_PREVIEW.tabHospitals, panelId: PUBLIC_PORTAL_PREVIEW.panelHospitals },
  { id: "volunteer", label: "Volunteer", icon: HelpingHand, testId: PUBLIC_PORTAL_PREVIEW.tabVolunteer, panelId: PUBLIC_PORTAL_PREVIEW.panelVolunteer },
  { id: "support", label: "Support Requests", icon: LifeBuoy, testId: PUBLIC_PORTAL_PREVIEW.tabSupport, panelId: PUBLIC_PORTAL_PREVIEW.panelSupport },
];

// Persistent, unmistakable preview indicator.
function PreviewBanner() {
  return (
    <div
      data-testid={PUBLIC_PORTAL_PREVIEW.banner}
      className="sticky top-16 z-30 bg-[#0B132B] text-white"
      role="status"
      aria-live="polite"
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
        <div className="flex items-center gap-2 min-w-0">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#2D9B4E] text-[10px] font-bold uppercase tracking-[0.14em]">
            <Sparkles className="w-3 h-3" />
            BTC Portal Preview
          </span>
          <span className="text-[12.5px] text-white/85 truncate">
            You're exploring a sample experience. Create an account to make BTC yours.
          </span>
        </div>
        <div className="flex items-center gap-2 sm:ml-auto shrink-0">
          <Link
            to="/register"
            data-testid={PUBLIC_PORTAL_PREVIEW.ctaJoin}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#2D9B4E] text-white text-[11px] font-bold uppercase tracking-[0.08em] hover:bg-[#23803F] transition"
          >
            <UserPlus className="w-3 h-3" /> Join BTC
          </Link>
          <Link
            to="/support"
            data-testid={PUBLIC_PORTAL_PREVIEW.ctaSupport}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/40 text-white text-[11px] font-bold uppercase tracking-[0.08em] hover:bg-white/10 transition"
          >
            <LifeBuoy className="w-3 h-3" /> Get Support
          </Link>
          <Link
            to="/"
            data-testid={PUBLIC_PORTAL_PREVIEW.ctaExit}
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white/80 text-[11px] font-semibold uppercase tracking-[0.08em] hover:text-white transition"
          >
            Return to BTC
          </Link>
        </div>
      </div>
    </div>
  );
}

function SampleBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F8F9FB] text-[9px] font-bold uppercase tracking-[0.12em] text-[#4B5563]">
      <Info className="w-2.5 h-2.5" /> Sample
    </span>
  );
}

// ----------------- Panels -----------------

function DashboardPanel({ data }) {
  if (!data) return <PanelLoading />;
  return (
    <div data-testid={PUBLIC_PORTAL_PREVIEW.panelDashboard} className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#23803F] mb-1 flex items-center gap-2">
            Today <SampleBadge />
          </p>
          <h1 className="text-2xl lg:text-3xl font-extrabold text-[#0B132B] tracking-tight">
            Welcome to BTC.
          </h1>
          <p className="text-[#4B5563] mt-1 text-sm">A sample of how a member's day begins here.</p>
        </div>
        <Link to="/support" className="btc-btn-primary text-xs">Get Support <ArrowRight className="w-3.5 h-3.5" /></Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {data.stats.map((s) => (
          <div key={s.id} className="bg-white rounded-2xl p-4 border border-gray-100">
            <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-semibold">{s.label}</div>
            <div className="text-2xl font-extrabold text-[#0B132B] mt-1">{s.value}</div>
            <div className="text-[11px] mt-1 font-medium text-[#23803F]">{s.note}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-5 border border-gray-100 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-[#0B132B]">Wellness journey (sample)</h3>
            <span className="text-[11px] text-[#4B5563]">Last 12 weeks</span>
          </div>
          <div className="h-32 flex items-end gap-1.5">
            {data.journey.map((h, i) => (
              <div key={i} className="flex-1 rounded-t-md bg-gradient-to-t from-[#2D9B4E]/80 to-[#2D9B4E]" style={{ height: `${h}%` }} />
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <h3 className="font-bold text-[#0B132B] mb-3">Today's actions</h3>
          <ul className="space-y-2.5">
            {data.today_actions.map((t) => (
              <li key={t} className="flex items-start gap-2 text-[13px] text-[#0B132B]">
                <CheckCircle2 className="w-4 h-4 text-[#23803F] mt-0.5" /> {t}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function MessagingPanel({ data }) {
  if (!data) return <PanelLoading />;
  return (
    <div data-testid={PUBLIC_PORTAL_PREVIEW.panelMessaging} className="space-y-4">
      <div className="flex items-center gap-3">
        <h2 className="text-xl font-extrabold text-[#0B132B]">Messaging</h2>
        <SampleBadge />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-2">
          {data.threads.map((t) => (
            <div
              key={t.id}
              className={`flex items-center gap-3 p-3 rounded-xl ${
                t.id === data.active_thread_id ? "bg-[#F8F9FB]" : "hover:bg-[#F8F9FB]"
              }`}
            >
              <div className="w-9 h-9 rounded-full text-white flex items-center justify-center text-xs font-bold" style={{ backgroundColor: t.color }}>
                {t.name[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#0B132B] truncate">{t.name}</div>
                <div className="text-xs text-[#4B5563] truncate">{t.last}</div>
              </div>
              {t.unread > 0 && (
                <span className="text-[10px] bg-[#2D9B4E] text-white px-1.5 rounded-full">{t.unread}</span>
              )}
            </div>
          ))}
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 flex flex-col h-[420px]">
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="font-semibold text-[#0B132B] text-sm">Care Team (sample)</div>
            <div className="text-xs text-[#23803F]">3 members · Active</div>
          </div>
          <div className="flex-1 p-4 space-y-3 overflow-y-auto">
            {data.messages.map((m) => (
              <div key={m.id} className={`max-w-[75%] ${m.mine ? "ml-auto" : ""}`}>
                <div
                  className={`rounded-2xl px-4 py-2.5 text-sm ${
                    m.mine ? "bg-[#2D9B4E] text-white rounded-tr-sm" : "bg-[#F8F9FB] text-[#0B132B] rounded-tl-sm"
                  }`}
                >
                  {m.body}
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-gray-100 flex gap-2 opacity-70">
            <input
              disabled
              placeholder="Sign in to send messages"
              className="flex-1 px-4 py-2 rounded-full bg-[#F8F9FB] border border-gray-100 text-sm cursor-not-allowed"
            />
            <span className="btc-btn-primary px-4 py-2 opacity-60 cursor-not-allowed">
              <Send className="w-4 h-4" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function CommunityPanel({ data }) {
  if (!data) return <PanelLoading />;
  return (
    <div data-testid={PUBLIC_PORTAL_PREVIEW.panelCommunity} className="space-y-4">
      <div className="flex items-center gap-3">
        <h2 className="text-xl font-extrabold text-[#0B132B]">Community</h2>
        <SampleBadge />
      </div>
      <p className="text-[#4B5563] text-sm">A sample of the groups BTC members explore.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.groups.map((g) => (
          <div key={g.id} className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#23803F] mb-2">{g.category}</div>
            <h3 className="font-bold text-[#0B132B] leading-snug">{g.name}</h3>
            <p className="text-sm text-[#4B5563] mt-2">{g.description}</p>
            <div className="flex items-center justify-between mt-4">
              <div className="text-xs text-[#4B5563]">{g.members.toLocaleString()} members</div>
              <span className="px-3 py-1.5 rounded-full text-white text-xs font-semibold opacity-70 cursor-not-allowed" style={{ backgroundColor: g.color }}>Join (sign in)</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ResourcesPanel({ data }) {
  if (!data) return <PanelLoading />;
  return (
    <div data-testid={PUBLIC_PORTAL_PREVIEW.panelResources} className="space-y-4">
      <div className="flex items-center gap-3">
        <h2 className="text-xl font-extrabold text-[#0B132B]">Resource Centre</h2>
        <SampleBadge />
      </div>
      <p className="text-[#4B5563] text-sm">A sample of the resources BTC curates. Personalized recommendations arrive after joining.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.items.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: r.color }}>{r.category}</div>
            <h3 className="font-bold text-[#0B132B] leading-snug mb-2">{r.title}</h3>
            <p className="text-sm text-[#4B5563]">{r.summary}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function EventsPanel({ data }) {
  if (!data) return <PanelLoading />;
  return (
    <div data-testid={PUBLIC_PORTAL_PREVIEW.panelEvents} className="space-y-4">
      <div className="flex items-center gap-3">
        <h2 className="text-xl font-extrabold text-[#0B132B]">Events</h2>
        <SampleBadge />
      </div>
      <p className="text-[#4B5563] text-sm">Sample gatherings and workshops. Public events published to /events remain the source of truth for the outside world.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.items.map((e) => (
          <div key={e.id} className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: e.color }}>{e.category}</div>
            <h3 className="font-bold text-[#0B132B] leading-snug">{e.title}</h3>
            <p className="text-sm text-[#4B5563] mt-2">{e.description}</p>
            <div className="mt-3 flex items-center gap-1.5 text-xs text-[#4B5563]">
              <Calendar className="w-3.5 h-3.5" /> {e.when}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HospitalsPanel({ data }) {
  if (!data) return <PanelLoading />;
  return (
    <div data-testid={PUBLIC_PORTAL_PREVIEW.panelHospitals} className="space-y-4">
      <div className="flex items-center gap-3">
        <h2 className="text-xl font-extrabold text-[#0B132B]">Connected Hospitals</h2>
        <SampleBadge />
      </div>
      <p className="text-[#4B5563] text-sm">Illustrative hospital directory. Real partner relationships appear only inside a signed-in member's portal.</p>
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F9FB] text-left text-[11px] uppercase tracking-wider text-[#4B5563]">
            <tr>
              <th className="px-4 py-3">Hospital</th>
              <th className="px-4 py-3 hidden sm:table-cell">Location</th>
              <th className="px-4 py-3 hidden md:table-cell">Patients</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((h) => (
              <tr key={h.id} className="border-t border-gray-100">
                <td className="px-4 py-3 font-semibold text-[#0B132B]">{h.name}</td>
                <td className="px-4 py-3 text-[#4B5563] hidden sm:table-cell">
                  <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {h.location}</span>
                </td>
                <td className="px-4 py-3 text-[#4B5563] hidden md:table-cell">{h.patients}</td>
                <td className="px-4 py-3">
                  <span className="text-[10px] font-bold bg-[#2D9B4E]/10 text-[#23803F] px-2 py-1 rounded-full">{h.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VolunteerPanel({ data }) {
  if (!data) return <PanelLoading />;
  return (
    <div data-testid={PUBLIC_PORTAL_PREVIEW.panelVolunteer} className="space-y-4">
      <div className="flex items-center gap-3">
        <h2 className="text-xl font-extrabold text-[#0B132B]">Volunteer Hub</h2>
        <SampleBadge />
      </div>
      <p className="text-[#4B5563] text-sm">A sample of BTC volunteer pathways. Applications are handled from Get Involved on the public site.</p>
      <div className="grid gap-3">
        {data.items.map((v) => (
          <div key={v.id} className="bg-white rounded-2xl border border-gray-100 p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="font-bold text-[#0B132B]">{v.title}</h3>
              <div className="text-xs text-[#1A5FAB] font-semibold mt-1">{v.commitment}</div>
              <p className="text-sm text-[#4B5563] mt-2">{v.description}</p>
            </div>
            <Link to="/get-involved" className="btc-btn-outline text-xs">Learn more</Link>
          </div>
        ))}
      </div>
    </div>
  );
}

function SupportPanel({ data }) {
  if (!data) return <PanelLoading />;
  return (
    <div data-testid={PUBLIC_PORTAL_PREVIEW.panelSupport} className="space-y-4">
      <div className="flex items-center gap-3">
        <h2 className="text-xl font-extrabold text-[#0B132B]">Support Requests</h2>
        <SampleBadge />
      </div>
      <p className="text-[#4B5563] text-sm">Read-only sample of how a member's support history looks. Starting a real request always flows through <Link to="/support" className="text-[#23803F] font-semibold underline decoration-[#2D9B4E]/40 underline-offset-4">Get Support</Link>.</p>
      <div className="space-y-3">
        {data.items.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#23803F]">{r.category}</div>
              <div className="text-xs text-[#4B5563]">{new Date(r.created_at).toLocaleDateString()}</div>
            </div>
            <p className="text-sm text-[#0B132B]">{r.excerpt}</p>
          </div>
        ))}
      </div>
      <div className="pt-2">
        <Link to="/support" className="btc-btn-primary text-sm">Start a real support request <ArrowRight className="w-4 h-4" /></Link>
      </div>
    </div>
  );
}

function PanelLoading() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="h-6 w-40 bg-gray-200 rounded" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 bg-gray-100 rounded-2xl" />
        ))}
      </div>
      <div className="h-40 bg-gray-100 rounded-2xl" />
    </div>
  );
}

// ----------------- Layout -----------------

export default function PortalPreview() {
  const [active, setActive] = useState("dashboard");
  const [payloads, setPayloads] = useState({});
  const [loadingId, setLoadingId] = useState(null);

  const fetchPreview = (id) => {
    if (payloads[id] || loadingId === id) return;
    setLoadingId(id);
    const pathMap = {
      dashboard: "/dashboard",
      messaging: "/messages",
      community: "/community",
      resources: "/resources",
      events: "/events",
      hospitals: "/hospitals",
      volunteer: "/volunteer",
      support: "/support-requests",
    };
    previewApi
      .get(pathMap[id])
      .then((r) => setPayloads((p) => ({ ...p, [id]: r.data })))
      .catch(() => setPayloads((p) => ({ ...p, [id]: { __error: true } })))
      .finally(() => setLoadingId(null));
  };

  useEffect(() => {
    fetchPreview(active);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const renderPanel = () => {
    const data = payloads[active];
    if (data?.__error) {
      return <div className="text-sm text-[#4B5563]">This preview panel couldn't load right now. Try another tab.</div>;
    }
    switch (active) {
      case "dashboard": return <DashboardPanel data={data} />;
      case "messaging": return <MessagingPanel data={data} />;
      case "community": return <CommunityPanel data={data} />;
      case "resources": return <ResourcesPanel data={data} />;
      case "events": return <EventsPanel data={data} />;
      case "hospitals": return <HospitalsPanel data={data} />;
      case "volunteer": return <VolunteerPanel data={data} />;
      case "support": return <SupportPanel data={data} />;
      default: return null;
    }
  };

  return (
    <div data-testid={PUBLIC_PORTAL_PREVIEW.page} className="bg-white min-h-screen flex flex-col">
      <Header />
      <PreviewBanner />

      <main id="main-content" className="flex-1 bg-[#F8F9FB]">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
          {/* Mini portal frame */}
          <div className="rounded-3xl border border-gray-200 bg-white overflow-hidden shadow-[0_20px_60px_-30px_rgba(11,19,43,0.18)]">
            <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] min-h-[640px]">
              {/* Sidebar */}
              <aside className="border-b lg:border-b-0 lg:border-r border-gray-100 bg-white px-3 py-4 lg:py-6">
                <div className="flex items-center gap-2 px-2 mb-5">
                  <BtcLogo className="h-8 w-auto" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#4B5563]">Portal Preview</span>
                </div>
                <nav
                  role="tablist"
                  aria-label="Portal preview sections"
                  className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-1 gap-1"
                >
                  {TABS.map((t) => {
                    const on = t.id === active;
                    return (
                      <button
                        key={t.id}
                        role="tab"
                        aria-selected={on}
                        aria-controls={t.panelId}
                        data-testid={t.testId}
                        onClick={() => setActive(t.id)}
                        className={`text-left flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2D9B4E]/60 ${
                          on
                            ? "bg-[#2D9B4E]/10 text-[#23803F] font-semibold"
                            : "text-[#0B132B] hover:bg-[#F8F9FB]"
                        }`}
                      >
                        <t.icon className="w-4 h-4 shrink-0" />
                        <span className="truncate">{t.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </aside>

              {/* Content */}
              <div className="min-w-0">
                <div className="h-14 border-b border-gray-100 px-4 lg:px-6 flex items-center gap-3">
                  <div className="relative flex-1 max-w-md hidden md:block">
                    <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      disabled
                      placeholder="Search is available for members"
                      className="w-full pl-9 pr-4 py-1.5 rounded-full bg-[#F8F9FB] border border-gray-100 text-xs text-gray-400 cursor-not-allowed"
                    />
                  </div>
                  <div className="flex-1 md:hidden" />
                  <span className="w-9 h-9 rounded-full bg-[#F8F9FB] flex items-center justify-center relative opacity-70">
                    <Bell className="w-4 h-4 text-[#0B132B]" />
                  </span>
                </div>
                <div
                  role="tabpanel"
                  id={TABS.find((t) => t.id === active)?.panelId}
                  className="p-4 lg:p-8"
                >
                  {renderPanel()}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom conversion band */}
          <section className="mt-10 lg:mt-14 rounded-3xl bg-[#0B132B] text-white overflow-hidden relative">
            <div className="absolute -top-24 -right-24 w-[360px] h-[360px] rounded-full bg-[#2D9B4E]/20 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-[320px] h-[320px] rounded-full bg-[#1A5FAB]/25 blur-3xl" />
            <div className="relative px-6 lg:px-14 py-14 lg:py-16 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
              <div>
                <p className="text-[#23803F] text-[11px] font-bold uppercase tracking-[0.2em] mb-3">
                  You've just seen a sample of BTC
                </p>
                <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-[1.05] mb-4">
                  Make BTC yours.
                </h2>
                <p className="text-white/75 max-w-xl">
                  Join to unlock a Portal that adapts to you — real conversations, real resources,
                  real people walking beside you. Need help first? Get Support is one click away.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-stretch">
                <Link to="/register" className="btc-btn-primary justify-center">
                  <UserPlus className="w-4 h-4" /> Join BTC <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/support" className="btc-btn-ghost-white justify-center">
                  <LifeBuoy className="w-4 h-4" /> Get Support
                </Link>
                <Link to="/" className="text-center text-white/70 text-xs font-semibold uppercase tracking-[0.14em] hover:text-white transition py-2">
                  Return to BTC
                </Link>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
